// src/components/KeyboardShortcutsModal.js
import React, { useEffect, useState } from 'react';
import { X, Keyboard } from 'lucide-react';
import ModalPortal from './ModalPortal';

const shortcuts = [
  {
    category: 'Navigation',
    items: [
      { keys: ['Ctrl', 'K'], description: 'Open global search' },
      { keys: ['?'], description: 'Show keyboard shortcuts' },
      { keys: ['Esc'], description: 'Close modal / Clear selection' },
    ],
  },
  {
    category: 'Table Actions',
    items: [
      { keys: ['Alt', 'N'], description: 'Add new item' },
      { keys: ['Delete'], description: 'Delete selected items' },
      { keys: ['Esc'], description: 'Clear selection' },
    ],
  },
  {
    category: 'Selection',
    items: [
      { keys: ['Ctrl', 'Click'], description: 'Toggle item selection' },
      { keys: ['Shift', 'Click'], description: 'Select range of items' },
      { keys: ['Right Click'], description: 'Open context menu' },
    ],
  },
];

const KeyboardShortcutsModal = ({ open, onClose }) => {
  if (!open) return null;

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <div
          className="w-full max-w-lg mx-4 bg-white dark:bg-zinc-900 rounded-xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center gap-2">
              <Keyboard className="w-5 h-5 text-zinc-500" />
              <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                Keyboard Shortcuts
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-zinc-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 max-h-[60vh] overflow-y-auto">
            {shortcuts.map((section, sectionIndex) => (
              <div key={section.category} className={sectionIndex > 0 ? 'mt-4' : ''}>
                <h3 className="text-[10px] uppercase tracking-wide font-medium text-zinc-500 dark:text-zinc-400 mb-2">
                  {section.category}
                </h3>
                <div className="space-y-1.5">
                  {section.items.map((shortcut, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                    >
                      <span className="text-xs text-zinc-700 dark:text-zinc-300">
                        {shortcut.description}
                      </span>
                      <div className="flex items-center gap-1">
                        {shortcut.keys.map((key, keyIndex) => (
                          <React.Fragment key={keyIndex}>
                            {keyIndex > 0 && (
                              <span className="text-[10px] text-zinc-400">+</span>
                            )}
                            <kbd className="px-1.5 py-0.5 text-[10px] font-mono font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 rounded border border-zinc-200 dark:border-zinc-700">
                              {key}
                            </kbd>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/30">
            <p className="text-[10px] text-zinc-500 dark:text-zinc-400 text-center">
              Press <kbd className="px-1 py-0.5 text-[9px] font-mono bg-zinc-200 dark:bg-zinc-700 rounded">?</kbd> anywhere to show this help
            </p>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

/**
 * Hook to manage keyboard shortcuts modal
 */
export const useKeyboardShortcutsModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Open on '?' key
      if (e.key === '?' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        setIsOpen(true);
      }

      // Close on Escape
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen((prev) => !prev),
  };
};

export default KeyboardShortcutsModal;
